import * as styles from "./ProjectTable.css";

import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Badge } from "@frontend/components/atoms/Badge";
import type { BadgeVariant } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
  useLocalDataTable,
} from "@frontend/components/organisms/DataTable";
import type { LocalDataTableColumn } from "@frontend/components/organisms/DataTable";
import { PdfViewerDialog } from "@frontend/components/organisms/PdfViewer";
import {
  ContractEditorDialog,
  contractApi,
} from "@frontend/features/contracts";
import type { ContractRow, ContractStatus } from "@frontend/features/contracts";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { formatAmount, parseAmount } from "@frontend/utils/amount";
import { Trans, useLingui } from "@lingui/react/macro";

import { syncClosurePlannableAtom } from "../stores/editor";
import {
  deleteContractAtom,
  loadContractsAtom,
  projectListsAtom,
  refreshContractsAtom,
} from "../stores/lists";

export type ContractListProps = {
  projectId: number;
};

const helper = createDataTableColumnHelper<ContractRow>();

const TONES: Record<ContractStatus, BadgeVariant> = {
  current: "success",
  future: "info",
  terminated: "neutral",
};

const RANKS: Record<ContractStatus, number> = {
  current: 0,
  future: 1,
  terminated: 2,
};

export function ContractList({ projectId }: ContractListProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const { contracts, contractsLoading, error } = useAtomValue(projectListsAtom);
  const load = useSetAtom(loadContractsAtom);
  const refresh = useSetAtom(refreshContractsAtom);
  const syncClosurePlannable = useSetAtom(syncClosurePlannableAtom);

  const [openContractId, setOpenContractId] = useState<
    number | null | undefined
  >();

  const [removing, setRemoving] = useState<ContractRow | null>(null);
  const [viewing, setViewing] = useState<ContractRow | null>(null);

  const remove = useSetAtom(deleteContractAtom);

  // The trash the question grows from.
  const originRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    void load(projectId);
  }, [load, projectId]);

  const money = (value: string | null): string =>
    null === value
      ? "-"
      : `${formatAmount(parseAmount(value) ?? 0, locale)} CHF`;

  const statusOptions = useMemo(
    () => [
      { value: "current", label: t`Running` },
      { value: "future", label: t`Future` },
      { value: "terminated", label: t`Terminated` },
    ],
    [t],
  );

  const statusLabel = useMemo(
    () =>
      Object.fromEntries(
        statusOptions.map(({ value, label }) => [value, label]),
      ) as Record<ContractStatus, string>,
    [statusOptions],
  );

  const readers = useMemo(
    (): Record<string, LocalDataTableColumn<ContractRow>> => ({
      version: { value: (contract) => contract.version },
      status: {
        value: (contract) => contract.status,
        searchable: false,
        sortValue: (contract) => RANKS[contract.status],
      },
      dateBegin: { value: (contract) => contract.dateBegin, searchable: false },
      realEndDate: {
        value: (contract) => contract.realEndDate,
        searchable: false,
      },
      priceAtDate: {
        value: (contract) => contract.priceAtDate,
        sortValue: (contract) => parseAmount(contract.priceAtDate) ?? 0,
      },
      amendmentCount: {
        value: (contract) => contract.amendmentCount,
        searchable: false,
      },
    }),
    [],
  );

  const local = useLocalDataTable({
    rows: contracts,
    columns: readers,
    initialSorting: [{ id: "version", desc: true }],
  });

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("version", {
          header: t`Version`,
          meta: { minWidth: "120px", maxWidth: "140px", filter: "number" },
        }),
        helper.accessor("status", {
          header: t`Status`,
          meta: {
            width: "130px",
            filter: { type: "select", options: statusOptions },
          },
          cell: ({ getValue }) => (
            <Badge variant={TONES[getValue()]} size="sm">
              {statusLabel[getValue()]}
            </Badge>
          ),
        }),
        helper.accessor("dateBegin", {
          header: t`From`,
          meta: { minWidth: "110px", maxWidth: "130px" },
          cell: ({ getValue }) => getValue() ?? "-",
        }),
        helper.accessor("realEndDate", {
          header: t`To`,
          meta: { minWidth: "110px", maxWidth: "130px" },
          cell: ({ getValue }) =>
            getValue() ?? <span className={styles.muted}>{t`Open`}</span>,
        }),
        helper.accessor("priceAtDate", {
          header: t`Price`,
          meta: { minWidth: "120px", maxWidth: "150px" },
          cell: ({ getValue }) => money(getValue()),
        }),
        helper.accessor("amendmentCount", {
          header: t`Amendments`,
          meta: { minWidth: "150px" },
          cell: ({ row, getValue }) =>
            0 === getValue() ? (
              <span className={styles.muted}>-</span>
            ) : (
              <span className={styles.name}>
                {getValue()}

                <span className={styles.muted}>
                  {0 <= (parseAmount(row.original.priceDiff) ?? 0) ? "+" : "-"}{" "}
                  {money(row.original.priceDiff).replace("-", "")}
                </span>
              </span>
            ),
        }),
        helper.display({
          id: "actions",
          header: t`Action`,
          enableSorting: false,
          meta: { width: "110px" },
          cell: ({ row }) => (
            <span
              className={styles.rowActions}
              onClick={(event) => event.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Print the contract`}
                      onClick={() => setViewing(row.original)}
                    >
                      <Icon name="Printer" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="left">
                  {t`Print the contract`}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Delete the contract`}
                      onClick={(event) => {
                        originRef.current =
                          event.currentTarget.getBoundingClientRect();
                        setRemoving(row.original);
                      }}
                    >
                      <Icon name="Trash2" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="left">
                  {t`Delete the contract`}
                </TooltipContent>
              </Tooltip>
            </span>
          ),
        }),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale, statusOptions, statusLabel],
  );

  const version = removing?.version ?? "";

  return (
    <div className={styles.table}>
      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      <Panel overflow="visible">
        <Toolbar wrap={false}>
          <ToolbarSection>
            <Button
              variant="link"
              size="sm"
              leftSection={<Icon name="Plus" size="sm" />}
              onClick={(event) => {
                originRef.current = event.currentTarget.getBoundingClientRect();
                setOpenContractId(null);
              }}
            >
              {t`Sign the next contract`}
            </Button>
          </ToolbarSection>

          <ToolbarSection className={styles.search}>
            <Input
              value={local.search}
              placeholder={t`Search a version, a price`}
              leftSection={<Icon name="Search" size="sm" />}
              onValueChange={local.setSearch}
            />
          </ToolbarSection>
        </Toolbar>

        <DataTable
          columns={columns}
          data={local.rows}
          getRowId={(contract) => String(contract.id)}
          loading={contractsLoading}
          loadingRowCount={3}
          emptyMessage={
            "" === local.search && 0 === local.columnFilters.length ? (
              <Trans>This project has no contract yet.</Trans>
            ) : (
              <Trans>No contract matches this search.</Trans>
            )
          }
          size="sm"
          variant="ghost"
          horizontalSpacing="sm"
          verticalSpacing="sm"
          bordered={false}
          sorting={local.sorting}
          onSortingChange={local.onSortingChange}
          columnFilters={local.columnFilters}
          onColumnFiltersChange={local.onColumnFiltersChange}
          onRowClick={(contract, event) => {
            originRef.current = event.currentTarget.getBoundingClientRect();
            setOpenContractId(contract.id);
          }}
        />
      </Panel>

      <AlertDialog
        open={null !== removing}
        originRef={originRef}
        onOpenChange={(next) => {
          if (!next) {
            setRemoving(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this contract?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                Version {version} goes with its amendments and its terms of
                payment. This cannot be undone.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (null !== removing) {
                  void remove(removing.id);
                }

                setRemoving(null);
              }}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {null === viewing ? null : (
        <PdfViewerDialog
          open
          src={contractApi.contractPdfUrl(projectId, viewing.id)}
          name={`contract-${projectId}-v${viewing.version}.pdf`}
          thumbnails
          onOpenChange={(next) => {
            if (!next) {
              setViewing(null);
            }
          }}
        />
      )}

      {undefined === openContractId ? null : (
        <ContractEditorDialog
          key={openContractId ?? "draft"}
          open
          onOpenChange={(next) => {
            if (!next) {
              setOpenContractId(undefined);
            }
          }}
          projectId={projectId}
          contractId={openContractId}
          onSaved={() => {
            setOpenContractId(undefined);

            void refresh();
            void syncClosurePlannable();
          }}
          onCancel={() => setOpenContractId(undefined)}
          originRef={originRef}
        />
      )}
    </div>
  );
}
